import { CustomContractFunnelItemDto, FeesReferentialDto, SelectFunnelsBorrowerCommandDto } from "api/pricing";
import { pricingAPI } from "services/swagger-api";
import { MainBorrowerPricingState } from "../../types";
import { MainBorrowerPricingSyncActions } from "../types";
import { PricingWatchdog } from "../pricingWatchdog";

export interface OnAdjustmentOverrides {
  borrowerCommission?: string
  /** Nouveaux fees globaux à appliquer — seuls les funnels non-édités seront inclus dans contractFunnels */
  globalFees?: Record<string, number>
  modeLissageFraisIntermediation?: string
}

export const onAdjustment = async (
  actions: MainBorrowerPricingSyncActions,
  getState: () => MainBorrowerPricingState,
  pricingWatchdog: PricingWatchdog,
  overrides?: OnAdjustmentOverrides
): Promise<void> => {
  pricingWatchdog.cancel()
  try {
    actions.setPriceLoading(true)
    actions.setError(null)

    const { pendingLoanConfigs, borrowerId, adjustmentsConfig, contractFunnels } = getState()

    const borrowerCommission = overrides?.borrowerCommission ?? adjustmentsConfig.borrowerCommission

    // Si des fees globaux sont passés en override, ne renvoyer que les funnels non-édités
    // avec les nouveaux fees appliqués. Les funnels édités sont exclus.
    // Sinon, renvoyer tous les funnels avec leurs fees locaux actuels.
    let customContractFunnels: CustomContractFunnelItemDto[]

    if (overrides?.globalFees) {
      const newGlobalFees = overrides.globalFees

      customContractFunnels = contractFunnels
        .filter(f => {
          if (!f.hash || !f.fees?.length) return false
          // On n'inclut que les non-édités (isEdition géré par le back)
          return !f.isEdition
        })
        .map(f => ({
          hash: f.hash!,
          fees: f.fees!.map(fee => ({
            code: fee.code,
            value: fee.code in newGlobalFees ? newGlobalFees[fee.code] : fee.value
          }))
        }))
    } else {
      customContractFunnels = contractFunnels
        .filter(f => f.hash && f.fees?.length)
        .map(f => ({
          hash: f.hash!,
          fees: f.fees!.map(fee => ({ code: fee.code, value: fee.value }))
        }))
    }

    // Construire les globalFees à partir des fees courants dans adjustmentsConfig
    const globalFees: FeesReferentialDto[] | undefined = adjustmentsConfig.fees?.length
      ? adjustmentsConfig.fees.map(f => ({ code: f.code, value: f.value }))
      : undefined

    const selectFunnelsBorrowerCommandDto: SelectFunnelsBorrowerCommandDto = {
      riskId: borrowerId || '',
      loans: pendingLoanConfigs,
      borrowerCommission,
      contractFunnels: customContractFunnels,
      globalFees,
      modeLissageFraisIntermediation: overrides?.modeLissageFraisIntermediation ?? adjustmentsConfig.modeLissageFraisIntermediation,
    }
    // Appeler le v2/custom-global pour obtenir les funnels avec pricing asynchrone
    const response = await pricingAPI.getBorrowerCustomPricingDataV2({ selectFunnelsBorrowerCommandDto })
    if (response.contractFunnels) {
      actions.updateContractFunnels(response.contractFunnels)
      pricingWatchdog.arm(async () =>
        (await pricingAPI.getBorrowerCustomPricingDataV2({ selectFunnelsBorrowerCommandDto })).contractFunnels)
    }
  } catch (error) {
    actions.setError(error instanceof Error ? error.message : 'Une erreur est survenue')
  } finally {
    actions.setPriceLoading(false)
  }
}
